import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Collapse, Space, Table, Tooltip, Typography } from "antd"
import { useState } from "react"
import VSpace from "../common/VSpace"
import { centsToDollarsDisplay } from "../displayFormatters"
import { useSoldJobRevenue } from "./dashboardQueries"
import dayjs from "dayjs"
import ExportCsvButton from "../Datacenter/ExportCsvButton"
import useDashboardReporting from "../../hooks/useDashboardReporting"
import { dashboardReportName, soldJobExportColumns } from "./dashboardExport"
import { DashboardFilters, hasDashboardFilters } from "./dashboardModel"

const { Text } = Typography

export type Props = {
  start: string
  end: string
  // The end sent to the API, when it differs from the date shown.
  queryEnd?: string
  // dashboard-reporting only; without it the card loads exactly as before.
  filters?: DashboardFilters
}

const SoldJobRevenue = ({ start, end, queryEnd = end, filters }: Props) => {
  const query = useSoldJobRevenue(start, queryEnd, filters)
  const dashboardReporting = useDashboardReporting()
  const [areJobsShowing, setAreJobsShowing] = useState(false)
  const hasDate = !!start && !!end

  return (
    <div style={{ width: '440px' }}>
      <VSpace style={{ minWidth: '440px', textAlign: 'center' }}>
        <Space>
          <h1 style={{ font: '1.4em "Proza Libre' }}>Sold Job Revenue</h1>
          <Tooltip title="Try setting the date range to your annual sales cycle (i.e. Jan&nbsp;1 - Dec&nbsp;31) to compare your sales against your sales goal. Your sales goal can be found in your budget worksheet.">
            <FontAwesomeIcon icon={faCircleInfo} />
          </Tooltip>
        </Space>
        <span>
          {hasDate
            ? <div>From {start} to {end}</div>
            : <div>Please select a time range at the top of the page.</div>
          }
        </span>
        {filters && hasDashboardFilters(filters) && <Text type="secondary">Filtered by the report filters</Text>}
        {dashboardReporting && hasDate && <div><ExportCsvButton size="small" reportName={dashboardReportName('Sold Job Revenue', filters)} dateRange={{ start: dayjs(start), end: dayjs(end) }} columns={soldJobExportColumns} rows={query.data?.jobs ?? []} /></div>}
        <div style={{ fontSize: '20pt', marginTop: '20px' }}>
          {query.isLoading
            ? <span>Loading...</span>
            : filters && query.isError
              ? <span>Error loading data</span>
              : <strong>{centsToDollarsDisplay(query.data?.soldJobRevenue)}</strong>
          }
        </div>
        {hasDate &&
          <Collapse ghost expandIcon={() => <></>} items={[
            {
              key: 'jobs',
              label: <Typography.Link>{areJobsShowing ? 'Hide' : 'Show'} jobs included in this data</Typography.Link>,
              children: <Table
                size="small"
                dataSource={query.data?.jobs}
                rowKey={(x) => x.jobId}
                columns={[
                  { title: 'Job Number', dataIndex: 'jobNumber', render: (text, record) => <Typography.Link href={`/#!/${record.url}`}>{text}</Typography.Link> },
                  {
                    title: 'Name', dataIndex: 'name', render: (text, record) => <VSpace>
                      <Text>{text}</Text>
                      <Text type="secondary">{record.customerName}</Text>
                    </VSpace>
                  },
                  { title: 'Revenue', dataIndex: 'revenue', render: centsToDollarsDisplay }
                ]}
              />
            }
          ]} onChange={key => setAreJobsShowing(key[0] === 'jobs')} />
        }
      </VSpace>
    </div>
  )
}

export default SoldJobRevenue